import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import type { EntityType } from 'types/entity'

const AVATAR_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90"><rect width="90" height="90" fill="#e5f6ec"/><circle cx="45" cy="34" r="16" fill="#bfd4c8"/><path d="M12 88c4-19 17-28 33-28s29 9 33 28z" fill="#bfd4c8"/></svg>`,
  )

/* 16px stroke icons, the same family the Sales module uses (lucide). */
const PROFILE_ICONS: Record<string, ReactElement> = {
  building: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 7L2 7" />
    </>
  ),
  pin: (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  chart: <path d="M12 20V10M18 20V4M6 20v-4" />,
  tag: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r="0.5" fill="currentColor" />
    </>
  ),
}

function ProfileIcon({ name }: { name: keyof typeof PROFILE_ICONS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PROFILE_ICONS[name]}
    </svg>
  )
}

export interface ProfilePanelProps {
  entityType: EntityType
  entityId: string
  imageUrl?: string
  score?: number | null
  personName: string
  companyName: string
  mobile: string
  email: string
  city: string
  state: string
  country: string
  pinCode: string
  lastUpdate?: string
  pipelineCount?: number
  tags: string[]
  onPipelineClick?: () => void
  onMobileClick?: () => void
}

/** Left column of the view screen — CreateLeadView's `.entityLeft` block. */
export function ProfilePanel(props: ProfilePanelProps) {
  const {
    entityType,
    entityId,
    imageUrl,
    score,
    personName,
    companyName,
    mobile,
    email,
    city,
    state,
    country,
    pinCode,
    lastUpdate,
    pipelineCount,
    tags,
    onPipelineClick,
    onMobileClick,
  } = props

  const location = [city, state, country, pinCode].filter(Boolean).join(', ')

  return (
    <div className="entity-left">
      <div className="user-img scorebox">
        <img src={imageUrl || AVATAR_FALLBACK} alt={personName || 'Profile'} onError={(e) => { e.currentTarget.src = AVATAR_FALLBACK }} />
        {score != null && <span className="scorecard" title="Lead score">{score}</span>}
      </div>

      <h4>{personName || '—'}</h4>

      {companyName && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="building" /></span>
          <span className="value">{companyName}</span>
        </p>
      )}

      {mobile && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="phone" /></span>
          <span className="value">
            <button type="button" className="link-btn" onClick={onMobileClick} title="Call will connect through the Mobile Like Number gateway.">
              {mobile}
            </button>
          </span>
        </p>
      )}

      {email && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="mail" /></span>
          <span className="value">
            <a href={`mailto:${email}`}>{email}</a>
          </span>
        </p>
      )}

      {location && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="pin" /></span>
          <span className="value">{location}</span>
        </p>
      )}

      {lastUpdate && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="calendar" /></span>
          <span className="value">
            <span className="label">Last Updated:</span>
            {lastUpdate}
          </span>
        </p>
      )}

      {pipelineCount != null && (
        <p className="entity-inner-detail">
          <span className="ico"><ProfileIcon name="chart" /></span>
          <span className="value">
            <span className="label">Pipeline:</span>
            <button type="button" className="link-btn" onClick={onPipelineClick}>{pipelineCount}</button>
          </span>
        </p>
      )}

      <div className="entity-inner-detail">
        <span className="ico"><ProfileIcon name="tag" /></span>
        <span className="value tag-list">
          {tags.length > 0 ? (
            tags.map((tag, i) => (
              <span key={`${tag}-${i}`} className={`tagcont ${i === 0 ? 'hot' : 'warm'}`}>
                {tag}
              </span>
            ))
          ) : (
            <Link className="taglink" to={`/edit/${entityType}/${entityId}`}>
              Click here to Add Tags
            </Link>
          )}
        </span>
      </div>
    </div>
  )
}
